import type { OrderState, PaymentMethod, TableOrder } from './types';

export type Action =
  | { type: 'ADD_ITEM'; table: number; itemId: string }
  | { type: 'CHANGE_QTY'; table: number; itemId: string; delta: number }
  | { type: 'SEND_TO_KITCHEN'; table: number; timestamp: number }
  | { type: 'MARK_DELIVERED'; table: number }
  | { type: 'PAY_TABLE'; table: number; method: PaymentMethod }
  | { type: 'TOGGLE_ITEM_AVAILABILITY'; itemId: string };

export function reducer(state: OrderState, action: Action): OrderState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existing: TableOrder = state.tables[action.table] ?? {
        tableNumber: action.table,
        items: [],
      };
      const idx = existing.items.findIndex((i) => i.menuItemId === action.itemId && !i.sent);
      let items;
      if (idx !== -1) {
        items = existing.items.map((item, i) =>
          i === idx ? { ...item, quantity: item.quantity + 1 } : item
        );
      } else {
        items = [
          ...existing.items,
          { menuItemId: action.itemId, quantity: 1, sent: false, delivered: false },
        ];
      }
      return {
        ...state,
        tables: { ...state.tables, [action.table]: { ...existing, items } },
      };
    }

    case 'CHANGE_QTY': {
      const existing = state.tables[action.table];
      if (!existing) return state;
      const idx = existing.items.findIndex((i) => i.menuItemId === action.itemId);
      if (idx === -1) return state;
      const current = existing.items[idx];
      const newQty = current.quantity + action.delta;
      let items;
      if (newQty <= 0) {
        items = existing.items.filter((_, i) => i !== idx);
      } else if (action.delta > 0 && current.sent && current.delivered) {
        // Re-ordering more of an already-served item sends it through the kitchen again.
        items = existing.items.map((item, i) =>
          i === idx ? { ...item, quantity: newQty, sent: false, delivered: false } : item
        );
      } else {
        items = existing.items.map((item, i) => (i === idx ? { ...item, quantity: newQty } : item));
      }
      return {
        ...state,
        tables: { ...state.tables, [action.table]: { ...existing, items } },
      };
    }

    case 'SEND_TO_KITCHEN': {
      const existing = state.tables[action.table];
      if (!existing) return state;
      const items = existing.items.map((item) => (item.sent ? item : { ...item, sent: true }));
      return {
        ...state,
        tables: {
          ...state.tables,
          [action.table]: { ...existing, items, lastSentAt: action.timestamp },
        },
      };
    }

    case 'MARK_DELIVERED': {
      const existing = state.tables[action.table];
      if (!existing) return state;
      const items = existing.items.map((item) => ({ ...item, delivered: true }));
      return {
        ...state,
        tables: { ...state.tables, [action.table]: { ...existing, items } },
      };
    }

    case 'PAY_TABLE': {
      const tables = { ...state.tables };
      delete tables[action.table];
      return { ...state, tables };
    }

    case 'TOGGLE_ITEM_AVAILABILITY': {
      const unavailableItemIds = { ...state.unavailableItemIds };
      if (unavailableItemIds[action.itemId]) {
        delete unavailableItemIds[action.itemId];
      } else {
        unavailableItemIds[action.itemId] = true;
      }
      return { ...state, unavailableItemIds };
    }

    default:
      return state;
  }
}
